import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { ArrowLeft, Plus, Users, ExternalLink, Pencil, Trash2, Copy, UserPlus, X, Upload, FileDown, Globe, Webhook, Send, CheckCircle2, LayoutGrid, ShieldCheck, Loader2, AlertTriangle, Undo2, Mail, Eye, MailCheck, Clock } from "lucide-react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";

export default function ClientAccountSeats() {
  const { accId } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [cards, setCards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ full_name: "", job_title: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [domain, setDomain] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [cname, setCname] = useState("cards.iwurks360.app");
  const [deliveries, setDeliveries] = useState([]);
  const [mapState, setMapState] = useState({ open: false });
  const [preview, setPreview] = useState({ open: false });
  const [bulkSending, setBulkSending] = useState(false);
  const csvRef = useRef();

  const load = useCallback(async () => {
    const [a, c, d] = await Promise.all([
      api.get(`/client-accounts/${accId}`),
      api.get(`/client-accounts/${accId}/cards`),
      api.get(`/client-accounts/${accId}/deliveries`).catch(() => ({ data: [] })),
    ]);
    setAccount(a.data); setCards(c.data); setDeliveries(d.data);
    setDomain(a.data.domain || ""); setWebhookUrl(a.data.webhook_url || "");
  }, [accId]);
  useEffect(() => { load().catch(() => toast.error("Account not found")); }, [load]);

  useEffect(() => {
    if (account?.ssl_status === "provisioning") {
      const t = setTimeout(() => load(), 4000);
      return () => clearTimeout(t);
    }
  }, [account?.ssl_status, load]);

  const full = account && account.seats_used >= account.seat_limit;

  const allocate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/client-accounts/${accId}/seats`, form);
      toast.success("Seat allocated — employee card created");
      setShowForm(false);
      setForm({ full_name: "", job_title: "", email: "", phone: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not allocate seat"); }
    finally { setSaving(false); }
  };

  const onCsvSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/client-accounts/${accId}/bulk-preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const guessFor = { full_name: ["full_name", "name", "full name", "fullname", "employee name", "employee", "staff name", "contact name"], job_title: ["job_title", "title", "role", "position", "designation"], email: ["email", "e-mail", "mail", "email address"], phone: ["phone", "mobile", "tel", "cell", "phone number", "contact number"], department: ["department", "dept", "team", "division", "unit"] };
      const autoMap = {};
      Object.entries(guessFor).forEach(([t, cands]) => { const h = (data.headers || []).find((x) => cands.includes(x.toLowerCase())); if (h) autoMap[t] = h; });
      setMapState({ open: true, file, headers: data.headers || [], rows: data.rows || [], mapping: autoMap });
    } catch { toast.error("Could not read that CSV file"); }
  };

  const runImport = async () => {
    const fd = new FormData();
    fd.append("file", mapState.file);
    fd.append("mapping", JSON.stringify(mapState.mapping));
    try {
      const { data } = await api.post(`/client-accounts/${accId}/bulk-import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} cards${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      if (data.seats_exceeded) toast.warning("Some rows exceeded the seat limit and were skipped");
      setMapState({ open: false });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
  };

  const downloadTemplate = () => {
    const csv = "full_name,job_title,email,phone\nJane Doe,Sales Director,jane@company.com,+1 555 0100\nJohn Roe,Account Executive,john@company.com,+1 555 0101\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "iwurks360_team_template.csv"; a.click();
  };

  const saveDomain = async () => {
    try { const { data } = await api.put(`/client-accounts/${accId}/domain`, { domain }); setCname(data.cname_target); toast.success("Domain saved — add the DNS record, then verify"); load(); }
    catch { toast.error("Could not save domain"); }
  };
  const verifyDomain = async () => {
    try { await api.post(`/client-accounts/${accId}/domain/verify`); toast.success("Domain verified & active"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
  };
  const saveWebhook = async () => {
    try { await api.put(`/client-accounts/${accId}/webhook`, { webhook_url: webhookUrl }); toast.success("Webhook saved"); load(); }
    catch { toast.error("Could not save webhook"); }
  };
  const testWebhook = async () => {
    try { await api.post(`/client-accounts/${accId}/webhook/test`); toast.success("Test lead sent"); setTimeout(load, 1500); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const del = async (id) => { if (!window.confirm("Remove this employee card and free the seat?")) return; await api.delete(`/cards/${id}`); toast.success("Seat freed"); load(); };
  const resendWelcome = async (id) => {
    try { const { data } = await api.post(`/client-accounts/${accId}/cards/${id}/resend-welcome`); toast.success(`Welcome email sent to ${data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send welcome email"); }
  };
  const previewWelcome = async (c) => {
    setPreview({ open: true, loading: true, name: c.full_name, cardId: c.id, hasEmail: !!c.email });
    try { const { data } = await api.get(`/client-accounts/${accId}/cards/${c.id}/welcome-preview`); setPreview({ open: true, loading: false, name: c.full_name, cardId: c.id, html: data.html, toEmail: data.to_email, hasEmail: data.has_email }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load preview"); setPreview({ open: false }); }
  };
  const resendAll = async () => {
    if (!window.confirm("Resend the welcome email to every team member who has an email on their card?")) return;
    setBulkSending(true);
    try { const { data } = await api.post(`/client-accounts/${accId}/resend-welcome-all`); toast.success(`Welcome email sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.skipped ? `, skipped ${data.skipped} without email` : ""}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send welcome emails"); }
    finally { setBulkSending(false); }
  };
  const copyReg = () => { navigator.clipboard.writeText(`${window.location.origin}/join/${account.reg_token}`); toast.success("Self-registration link copied"); };
  const copyDir = () => { navigator.clipboard.writeText(`${window.location.origin}/d/${account.reg_token}`); toast.success("Directory link copied"); };
  const copyCard = (slug) => { navigator.clipboard.writeText(`${window.location.origin}/c/${slug}`); toast.success("Card link copied"); };
  const undoImport = async () => {
    if (!window.confirm("Undo the last import and delete those imported cards?")) return;
    try { const { data } = await api.post(`/client-accounts/${accId}/imports/undo`); toast.success(`Removed ${data.deleted} imported cards`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Nothing to undo"); }
  };

  if (!account) return <DashboardLayout title="Client Account"><p className="text-[#8B9BB4]">Loading…</p></DashboardLayout>;

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };
  const pct = Math.min(100, (account.seats_used / account.seat_limit) * 100);

  return (
    <DashboardLayout title={account.company_name}
      actions={<div className="flex flex-wrap gap-2">
        <button onClick={() => navigate("/app/clients")} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
        <button onClick={() => csvRef.current.click()} disabled={full} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2 disabled:opacity-40" data-testid="bulk-import-btn"><Upload size={16} /> Import CSV</button>
        <input ref={csvRef} type="file" accept=".csv" hidden onChange={onCsvSelected} data-testid="csv-input" />
        {cards.length > 0 && <button onClick={resendAll} disabled={bulkSending} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2 disabled:opacity-40" data-testid="resend-all-btn">{bulkSending ? <Loader2 size={16} className="animate-spin" /> : <MailCheck size={16} />} Resend to all</button>}
        <button onClick={() => setShowForm(true)} disabled={full} className="btn-gold px-4 py-2.5 rounded-full text-sm flex items-center gap-2 disabled:opacity-40" data-testid="allocate-seat-btn"><UserPlus size={16} /> Allocate Seat</button>
      </div>}>
      <div className="fade-up space-y-6">
        <div className="iw-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="overline">{account.plan.replace("_", " ")} plan</span>
              {account.domain && <p className="text-sm text-[#8B9BB4] mt-1">{account.domain}</p>}
            </div>
          </div>
          <div className="mt-5 max-w-md">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="flex items-center gap-1.5 text-[#8B9BB4]"><Users size={14} /> Seats used</span>
              <span className="text-[#F6F1E4] font-semibold" data-testid="seat-count">{account.seats_used} / {account.seat_limit}</span>
            </div>
            <div className="h-2 rounded-full bg-[#142B5A] overflow-hidden">
              <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${pct}%` }} />
            </div>
            {full && <p className="text-xs text-red-400 mt-2">All seats used — upgrade the plan to add more.</p>}
          </div>
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-[#ffffff1a]">
            <button onClick={copyReg} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="copy-reg-link-btn"><Copy size={14} /> Self-registration link</button>
            <button onClick={copyDir} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="copy-dir-link-btn"><Copy size={14} /> Team directory link</button>
            <a href={`/d/${account.reg_token}`} target="_blank" rel="noreferrer" className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2"><LayoutGrid size={14} /> View Directory</a>
            <button onClick={downloadTemplate} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="csv-template-btn"><FileDown size={14} /> CSV template</button>
            {account.last_import && !account.last_import.undone && account.last_import.count > 0 && (
              <button onClick={undoImport} className="px-4 py-2.5 rounded-full text-sm flex items-center gap-2 text-red-300 border border-red-400/40 hover:bg-red-400/10 transition-colors" data-testid="undo-import-btn"><Undo2 size={14} /> Undo last import ({account.last_import.count})</button>
            )}
          </div>
        </div>

        {/* Custom domain + CRM webhook */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="iw-card p-6">
            <p className="overline flex items-center gap-2"><Globe size={14} /> Custom Domain</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-4">Serve this client's cards under their own branded web address.</p>
            <div className="flex gap-2">
              <input className="iw-input" placeholder="cards.acmeglobal.com" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="domain-input" />
              <button onClick={saveDomain} className="btn-gold px-5 rounded-lg text-sm shrink-0" data-testid="save-domain-btn">Save</button>
            </div>
            {account.domain && (
              <div className="mt-4 text-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[#8B9BB4]">Status:</span>
                  {account.domain_status === "active"
                    ? <span className="text-green-400 flex items-center gap-1" data-testid="domain-status"><CheckCircle2 size={14} /> Active</span>
                    : <span className="text-[#D4AF37]" data-testid="domain-status">Pending verification</span>}
                </div>
                <div className="rounded-lg bg-[#081426] border border-[#ffffff1a] p-3 text-xs text-[#8B9BB4]">
                  <p className="mb-1">Add this DNS record at your registrar:</p>
                  <p className="text-[#F6F1E4]">CNAME &nbsp;<span className="text-[#D4AF37]">{account.domain}</span> &nbsp;→&nbsp; <span className="text-[#D4AF37]">{cname}</span></p>
                </div>
                {account.domain_status !== "active" && <button onClick={verifyDomain} className="btn-ghost px-4 py-2 rounded-full text-xs mt-3" data-testid="verify-domain-btn">Verify Domain</button>}
                {account.domain_status === "active" && (
                  <div className="mt-3 flex items-center gap-2" data-testid="ssl-status">
                    {account.ssl_status === "issued"
                      ? <span className="text-green-400 flex items-center gap-1.5 text-sm"><ShieldCheck size={15} /> SSL certificate issued — serving over HTTPS</span>
                      : <span className="text-[#D4AF37] flex items-center gap-1.5 text-sm"><Loader2 size={15} className="animate-spin" /> Provisioning SSL certificate…</span>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="iw-card p-6">
            <p className="overline flex items-center gap-2"><Webhook size={14} /> CRM Lead Webhook</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-4">Push every captured lead to Salesforce, HubSpot or any endpoint in real time.</p>
            {account.webhook_failing && (
              <div className="mb-4 rounded-lg border border-red-400/40 bg-red-400/10 p-3 flex items-center gap-2 text-sm text-red-300" data-testid="webhook-failing">
                <AlertTriangle size={16} /> This endpoint keeps failing — leads aren't being delivered. Check the URL.
              </div>
            )}
            <div className="flex gap-2">
              <input className="iw-input" placeholder="https://hooks.crm.com/leads" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} data-testid="webhook-input" />
              <button onClick={saveWebhook} className="btn-gold px-5 rounded-lg text-sm shrink-0" data-testid="save-webhook-btn">Save</button>
            </div>
            {account.webhook_url && <button onClick={testWebhook} className="btn-ghost px-4 py-2 rounded-full text-xs mt-3 flex items-center gap-2" data-testid="test-webhook-btn"><Send size={13} /> Send test lead</button>}
            {deliveries.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-[#8B9BB4] mb-2">Recent deliveries</p>
                <div className="space-y-1.5" data-testid="deliveries-list">
                  {deliveries.slice(0, 5).map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-xs">
                      <span className="text-[#F6F1E4] truncate">{d.lead_name || "Lead"}</span>
                      <span className={d.ok ? "text-green-400" : "text-red-400"}>{d.ok ? `✓ ${d.status}` : `✗ ${d.status || "failed"}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {cards.length === 0 ? (
          <div className="iw-card p-12 text-center">
            <UserPlus className="text-[#D4AF37] mx-auto mb-3" size={32} />
            <h3 className="font-display text-2xl font-bold">No employee cards yet</h3>
            <p className="text-[#8B9BB4] mt-2 mb-6">Allocate a seat to create a branded card for a team member, or share the self-registration link.</p>
            <button onClick={() => setShowForm(true)} className="btn-gold px-6 py-3 rounded-full">Allocate First Seat</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cards.map((c) => (
              <div key={c.id} className="iw-card p-6" data-testid={`seat-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold">{c.full_name || "Unnamed"}</h3>
                    <p className="text-sm text-[#D4AF37]">{c.job_title}</p>
                    <p className="text-xs text-[#8B9BB4] mt-0.5">{c.email}</p>
                  </div>
                  <span className="h-3 w-3 rounded-full" style={{ background: c.brand_color }} />
                </div>
                <div className="mt-3">
                  {c.first_viewed_at
                    ? <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-green-400/10 text-green-400 border border-green-400/25" data-testid={`onboard-status-${c.id}`}><CheckCircle2 size={12} /> Opened{c.view_count ? ` · ${c.view_count} view${c.view_count === 1 ? "" : "s"}` : ""} · {fmtDate(c.first_viewed_at)}</span>
                    : <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#142B5A] text-[#8B9BB4] border border-[#ffffff1a]" data-testid={`onboard-status-${c.id}`}><Clock size={12} /> Not opened yet</span>}
                </div>
                <div className="flex flex-wrap gap-2 mt-5">
                  <button onClick={() => navigate(`/app/cards/${c.id}`)} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"><Pencil size={13} /> Edit</button>
                  <a href={`/c/${c.slug}`} target="_blank" rel="noreferrer" className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"><ExternalLink size={13} /> View</a>
                  <button onClick={() => copyCard(c.slug)} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"><Copy size={13} /> Link</button>
                  <button onClick={() => previewWelcome(c)} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5" data-testid={`preview-welcome-${c.id}`}><Eye size={13} /> Preview email</button>
                  {c.email && <button onClick={() => resendWelcome(c.id)} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5" data-testid={`resend-welcome-${c.id}`}><Mail size={13} /> Resend welcome</button>}
                  <button onClick={() => del(c.id)} className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 transition-colors" data-testid={`remove-seat-${c.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70" onClick={() => setShowForm(false)}>
          <div className="iw-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold">Allocate a seat</h3>
              <button onClick={() => setShowForm(false)}><X size={18} className="text-[#8B9BB4]" /></button>
            </div>
            <p className="text-sm text-[#8B9BB4] mb-4">Creates an employee card under <span className="text-[#F6F1E4]">{account.company_name}</span>, inheriting the company brand.</p>
            <form onSubmit={allocate} className="space-y-3">
              <input className="iw-input" placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="seat-name" />
              <input className="iw-input" placeholder="Job title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="seat-title" />
              <input className="iw-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="seat-email" />
              <input className="iw-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="seat-phone" />
              <button disabled={saving} className="btn-gold w-full py-3 rounded-full" data-testid="seat-submit">{saving ? "Allocating…" : "Create Employee Card"}</button>
            </form>
          </div>
        </div>
      )}

      {mapState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70" onClick={() => setMapState({ open: false })}>
          <div className="iw-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="map-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold">Map your CSV columns</h3>
              <button onClick={() => setMapState({ open: false })}><X size={18} className="text-[#8B9BB4]" /></button>
            </div>
            <p className="text-sm text-[#8B9BB4] mb-5">Match your file's columns to card fields. We've guessed where we could — adjust anything that's off.</p>
            <div className="space-y-3">
              {[["full_name", "Full name *"], ["job_title", "Job title"], ["department", "Department"], ["email", "Email"], ["phone", "Phone"]].map(([key, label]) => (
                <div key={key} className="flex items-center gap-3">
                  <label className="text-sm text-[#F6F1E4] w-28 shrink-0">{label}</label>
                  <select className="iw-input" value={mapState.mapping[key] || ""} onChange={(e) => setMapState({ ...mapState, mapping: { ...mapState.mapping, [key]: e.target.value } })} data-testid={`map-${key}`}>
                    <option value="">— Not mapped —</option>
                    {mapState.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {mapState.rows?.length > 0 && mapState.mapping.full_name && (
              <div className="mt-5 rounded-lg bg-[#081426] border border-[#ffffff1a] p-3">
                <p className="text-xs text-[#8B9BB4] mb-2">Preview (first rows)</p>
                {mapState.rows.slice(0, 3).map((r, i) => (
                  <p key={`${r[mapState.mapping.full_name] || "row"}-${i}`} className="text-sm text-[#F6F1E4] truncate">{r[mapState.mapping.full_name] || "—"} <span className="text-[#8B9BB4]">· {r[mapState.mapping.job_title] || ""}</span></p>
                ))}
              </div>
            )}
            <button onClick={runImport} disabled={!mapState.mapping.full_name} className="btn-gold w-full py-3 rounded-full mt-6 disabled:opacity-40" data-testid="confirm-import-btn">Import Team</button>
          </div>
        </div>
      )}

      {preview.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70" onClick={() => setPreview({ open: false })}>
          <div className="iw-card p-6 w-full max-w-xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="welcome-preview-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-bold">Welcome email preview</h3>
              <button onClick={() => setPreview({ open: false })}><X size={18} className="text-[#8B9BB4]" /></button>
            </div>
            <p className="text-sm text-[#8B9BB4] mb-4">
              This is exactly what {preview.name || "this member"} receives.{" "}
              {preview.hasEmail
                ? <>It will be sent to <span className="text-[#F6F1E4]">{preview.toEmail}</span>.</>
                : <span className="text-[#D4AF37]">No email on this card yet — add one to send it.</span>}
            </p>
            {preview.loading ? (
              <div className="py-16 text-center text-[#8B9BB4]"><Loader2 size={22} className="animate-spin mx-auto" /></div>
            ) : (
              <div className="rounded-xl border border-[#ffffff1a] overflow-hidden bg-[#081426]" data-testid="welcome-preview-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html || "", { ADD_TAGS: ["table", "tr", "td", "img"], ADD_ATTR: ["cellpadding", "cellspacing", "width", "height", "align", "src", "alt", "style", "href"] }) }} />
            )}
            {preview.hasEmail && !preview.loading && (
              <button onClick={() => { resendWelcome(preview.cardId); setPreview({ open: false }); }} className="btn-gold w-full py-3 rounded-full mt-5 flex items-center justify-center gap-2" data-testid="preview-send-btn"><Send size={15} /> Send this welcome email now</button>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
